export class OrderManager {
  static orders;

  constructor(orders, isUrgent = false) {
    this.buildOrders(orders, isUrgent);
  }

  buildOrders(orders, isUrgent) {
    let newOrders = [];
    const combinations = this.formatOrders(orders.combinations, true, isUrgent);
    const singles = this.formatOrders(orders.single_services, false, isUrgent);
    newOrders.push(...combinations.concat(singles));
    if (isUrgent) {
      this.orders = newOrders;
    } else {
      this.orders = { exported: [], confirmed: [], nonConfirmed: [] };
      this.orders.confirmed = newOrders.filter(
        (order) => order.confirmed && !order.exported
      );
      this.orders.nonConfirmed = newOrders.filter(
        (order) => !order.exported && !order.confirmed
      );
      this.orders.exported = newOrders.filter((order) => order.exported);
    }
  }

  formatOrders(orders, combined, isUrgent) {
    return orders.map((order) => {
      const formattedOrder = this.createBaseFormattedOrder(order, combined);
      
      if (combined) {
        this.formatCombinedOrder(formattedOrder, order, isUrgent);
      } else {
        this.formatSingleOrder(formattedOrder, order);
      }

      this.setOrderId(formattedOrder, combined, order);
      return formattedOrder;
    });
  }

  createBaseFormattedOrder(order, combined) {
    return {
      type: combined ? "combined" : order.type.toLowerCase(),
      import: {},
      export: {},
      status: "ok",
      confirmed: order.confirmed,
      exported: order.exported,
      pending: false,
      flag_extra_day: order.flag_extra_day || false,
      id: "",
      long_term_plan: order.long_term_plan || false,
      ignore_in_model: order.ignore_in_model || null,
      supplier_id: order.id_supplier
    };
  }

  formatCombinedOrder(formattedOrder, order, isUrgent) {
    formattedOrder.pending = this.getCombinedOrderPendingStatus(order);
    formattedOrder.import = this.formatImportData(order, isUrgent);
    formattedOrder.export = this.formatExportData(order);
  }

  formatSingleOrder(formattedOrder, order) {
    formattedOrder.pending = order.flag_change;
    const orderType = order.type.toLowerCase();
    formattedOrder[orderType] = this.formatSingleOrderData(order);
  }

  getCombinedOrderPendingStatus(order) {
    return order.flag_break ||
           order.flag_change_import ||
           order.flag_change_export ||
           order.flag_route_change;
  }

  formatImportData(order, isUrgent) {
    const importData = {
      client_address: order.import_factory_address,
      container_type: order.import_container_type,
      depot: order.import_depot_name,
      id_depot: order.import_depot_id,
      id_port: order.import_id_port,
      id_management_area: order.import_management_area_id,
      id: order.import_service_id,
      commercial_number: order.import_commercial_number,
      km: order.base_km - order.km,
      savings_percentage: this.calculateSavingsPercentage(order),
      client: order.import_factory_name,
      postal_code_client: order.import_factory_postal_code,
      service_datetime: this.formatDateTime(order.import_service_datetime),
      datetime_container: this.formatDateTime(order.datetime_start),
      fabric_datetime: this.formatDateTime(order.import_end),
      client_town: order.import_factory_town,
      supplier: order.supplier_name,
      supplier_id: order.id_supplier,
      kpi: order.kpi?.toFixed(2),
      tilt: order.import_tilt,
      reinforced: order.import_need_reinforced,
      multi_step: order.import_multi_step,
      oversized: order.import_oversized,
      teco_to_truck: order.import_teco_to_truck || false,
      date_change: order.import_date_change || false,
      id_truck_plate: order.id_truck_plate,
      is_d_plus_one: order.flag_import_day || false,
    };

    if (isUrgent) {
      importData.single_end = this.formatDateTime(order.import_single_end);
    }

    return importData;
  }

  formatExportData(order) {
    return {
      client_address: order.export_factory_address,
      container_type: order.export_container_type,
      depot: order.export_depot_name,
      id_depot: order.export_depot_id,
      id_port: order.export_id_port,
      id_management_area: order.export_management_area_id,
      id: order.export_service_id,
      commercial_number: order.export_commercial_number,
      km: order.base_km - order.km,
      client: order.export_factory_name,
      postal_code_client: order.export_factory_postal_code,
      service_datetime: this.formatDateTime(order.export_service_datetime),
      datetime_container: this.formatDateTime(order.datetime_end),
      fabric_datetime: this.formatDateTime(order.export_start),
      client_town: order.export_factory_town,
      supplier: order.supplier_name,
      supplier_id: order.id_supplier,
      kpi: order.kpi?.toFixed(2),
      tilt: order.export_tilt,
      reinforced: order.export_need_reinforced,
      multi_step: order.export_multi_step,
      oversized: order.export_oversized,
      teco_to_truck: order.export_teco_to_truck || false,
      date_change: order.export_date_change || false,
      id_truck_plate: order.id_truck_plate,
      is_d_plus_one: order.flag_export_day || false,
    };
  }

  formatSingleOrderData(order) {
    const datetime_container = order.type === "IMPORT" ? order.datetime_start : order.datetime_end;
    const fabric_datetime = order.type === "IMPORT" ? order.import_end : order.export_start;

    return {
      client_address: order.factory_address,
      container_type: order.container_type,
      depot: order.depot_name,
      id_depot: order.id_depot,
      id_depot_empty: order.id_depot_empty,
      depot_empty_name: order.depot_empty_name,
      id_port: order.id_port,
      id_management_area: order.id_management_area,
      id: order.id,
      commercial_number: order.commercial_number,
      client: order.factory_name,
      postal_code_client: order.factory_postal_code,
      service_datetime: this.formatDateTime(order.service_datetime),
      datetime_container: this.formatDateTime(datetime_container),
      datetime_container_end: this.formatDateTime(order.datetime_end),
      datetime_container_start: this.formatDateTime(order.datetime_start),
      fabric_datetime: this.formatDateTime(fabric_datetime),
      single_end: null,
      client_town: order.factory_town,
      supplier: order.supplier_name,
      supplier_id: order.id_supplier,
      kpi: order.kpi?.toFixed(2),
      tilt: order.tilt,
      reinforced: order.need_reinforced,
      dangerous: order.is_dangerous,
      is_flexibag: order.is_flexibag,
      multi_step: order.multi_step,
      shipper_own: order.shipper_own,
      oversized: order.oversized,
      teco_to_truck: order.teco_to_truck || false,
      date_change: order.date_change || false,
      id_truck_plate: order.id_truck_plate,
      is_d_plus_one: !order.flag_extra_day,
    };
  }

  calculateSavingsPercentage(order) {
    return parseFloat((((order.base_km - order.km) / order.base_km) * 100).toFixed(2));
  }

  formatDateTime(datetime) {
    return OrderManager.formatDateTime(datetime);
  }

  // Static helper to format date/time; reusable from other components
  static formatDateTime(datetime) {
    if (datetime === null || datetime === undefined || datetime === '') {
      return null;
    }
    return new Date(datetime).toLocaleString("es-ES", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  setOrderId(formattedOrder, combined, order) {
    formattedOrder.id = combined
      ? formattedOrder.import.id
      : formattedOrder[order.type.toLowerCase()].id;
  }

  getOrderIds() {
    let orderIds = [];
    this.orders.exported.forEach((order) => {
      if (order.type === "combined") {
        orderIds.push(order.import.id);
        orderIds.push(order.export.id);
      } else {
        orderIds.push(order[order.type.toLowerCase()].id);
      }
    });
    this.orders.nonConfirmed.forEach((order) => {
      if (order.type === "combined") {
        orderIds.push(order.import.id);
        orderIds.push(order.export.id);
      } else {
        orderIds.push(order[order.type.toLowerCase()].id);
      }
    });
    this.orders.confirmed.forEach((order) => {
      if (order.type === "combined") {
        orderIds.push(order.import.id);
        orderIds.push(order.export.id);
      } else {
        orderIds.push(order[order.type.toLowerCase()].id);
      }
    });
    return [...new Set(orderIds)];
  }

  getCommercialNumbers() {
    let commercialNumber = [];
    this.orders.exported.forEach((order) => {
      if (order.type === "combined") {
        commercialNumber.push(order.import.commercial_number);
        commercialNumber.push(order.export.commercial_number);
      } else {
        commercialNumber.push(
          order[order.type.toLowerCase()].commercial_number
        );
      }
    });
    this.orders.nonConfirmed.forEach((order) => {
      if (order.type === "combined") {
        commercialNumber.push(order.import.commercial_number);
        commercialNumber.push(order.export.commercial_number);
      } else {
        commercialNumber.push(
          order[order.type.toLowerCase()].commercial_number
        );
      }
    });
    this.orders.confirmed.forEach((order) => {
      if (order.type === "combined") {
        commercialNumber.push(order.import.commercial_number);
        commercialNumber.push(order.export.commercial_number);
      } else {
        commercialNumber.push(
          order[order.type.toLowerCase()].commercial_number
        );
      }
    });
    return [...new Set(commercialNumber)];
  }

  getUrgentOrderIds() {
    let orderIds = [];
    this.orders.forEach((order) => {
      if (order.type === "combined") {
        orderIds.push(order.import.id);
        orderIds.push(order.export.id);
      } else {
        orderIds.push(order[order.type.toLowerCase()].id);
      }
    });

    return [...new Set(orderIds)];
  }

  getUrgentCommercialNumbers() {
    let commercialNumber = [];
    this.orders.forEach((order) => {
      if (order.type === "combined") {
        commercialNumber.push(order.import.commercial_number);
        commercialNumber.push(order.export.commercial_number);
      } else {
        commercialNumber.push(
          order[order.type.toLowerCase()].commercial_number
        );
      }
    });
    return [...new Set(commercialNumber)];
  }
  async getOrderSupplierFromId(orderId, supplierId) {
    let suppliers = await Promise.all([
      this.$store.dispatch("GET_SUPPLIERS_FOR_ORDER", {
        orderId: orderId,
      }),
    ]);
    suppliers = suppliers.flat();
    const supplier = suppliers.find((supplier) => supplier.id === supplierId);
    return supplier.name;
  }
}
